import { useEffect, useId, useRef, type ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { t } from '@/lib/i18n';
import { Button } from './Button';
import styles from './Overlay.module.css';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children?: ReactNode;
  /** Optional numeric consequence row — score, differential, resulting handicap. */
  stats?: ReactNode;
  /** The reassuring choice. Always the filled button. */
  confirmLabel: string;
  onConfirm: () => void;
  confirmBusy?: boolean;
  /** The way back. */
  cancelLabel?: string;
  /**
   * An irreversible alternative, rendered as red text rather than a button — so the
   * button-shaped target is never the destructive one (canvas 1m).
   */
  destructiveLabel?: string;
  onDestructive?: () => void;
  /** Puts confirm and cancel side by side, for a symmetric, low-stakes choice. */
  inline?: boolean;
}

/**
 * Built on the native <dialog> element: focus trapping, Escape, inertness of the page
 * behind it and the ::backdrop all come from the platform rather than from us.
 */
export function Dialog({
  open,
  onClose,
  title,
  children,
  stats,
  confirmLabel,
  onConfirm,
  confirmBusy = false,
  cancelLabel = t('record.cancel'),
  destructiveLabel,
  onDestructive,
  inline = false,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className={styles.dialog}
      aria-labelledby={titleId}
      // Fires on Escape and on backdrop dismissal, keeping React state in step.
      onClose={onClose}
      onCancel={onClose}
    >
      <h2 className={styles.dialogTitle} id={titleId}>
        {title}
      </h2>
      {children != null && <p className={styles.dialogBody}>{children}</p>}
      {stats != null && <div className={styles.dialogStats}>{stats}</div>}

      <div className={cx(styles.dialogActions, inline && styles.dialogActionsRow)}>
        {inline ? (
          <>
            <Button variant="secondary" onClick={onClose}>
              {cancelLabel}
            </Button>
            <Button onClick={onConfirm} busy={confirmBusy}>
              {confirmLabel}
            </Button>
          </>
        ) : (
          <>
            <Button block size="lg" onClick={onConfirm} busy={confirmBusy}>
              {confirmLabel}
            </Button>
            <Button block variant="secondary" onClick={onClose}>
              {cancelLabel}
            </Button>
          </>
        )}
      </div>

      {destructiveLabel && onDestructive && (
        <Button variant="danger" block onClick={onDestructive}>
          {destructiveLabel}
        </Button>
      )}
    </dialog>
  );
}
